import React, {useCallback, useEffect, useState} from "react";
import {Loader} from "../../components/Loader";
import {FolderPreview} from "./in-page-folders-preview";
import {useNavigationContext} from "../../contexts/NavContext/nav-context";
import {ManageGroupTags} from "./popup-update-codes";
import {useFolderContext} from "../../contexts/folder_context";
import {COMBINED_FOLDER_ID} from "../../schemas";
import {getFolderPreviews} from "../../api";
import {DOCUMENT_UPLOAD_COMPLETED} from "../../events";
import {FiFolderPlus, FiUpload} from "react-icons/fi";
import {ManageGroupActions} from "./clear-workspace";
import DashboardHeader, {useDashboardHeader} from "../../components/DashboardHeader";
import FolderUpload from "./FileUpload/popup-file-upload";
import PalettePanel from "../../components/Palettes/PalettePanel";

// Sidebar icon button component
const SidebarIconButton: React.FC<{
    icon: React.ReactNode;
    title: string;
    isActive: boolean;
    onClick: () => void;
    onMouseEnter?: () => void;
}> = ({icon, title, isActive, onClick, onMouseEnter}) => (
    <div className="p-2">
        <button
            type="button"
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            title={title}
            className={[
                "h-9 w-9 grid place-items-center rounded-lg",
                isActive
                    ? "bg-[#6a4eaa] text-white shadow-[0_4px_14px_-4px_rgba(106,78,170,0.55)]"
                    : "text-zinc-400 hover:text-white hover:bg-[#2a2046]",
                "transition duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9c83cf] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1c1530]",
            ].join(" ")}
        >
            <span className="text-[16px] leading-none">{icon}</span>
        </button>
    </div>
);

// Folder upload panel content
const FolderUploadPanel: React.FC = () => {
    const {closeLeftPanel} = useDashboardHeader();
    return (
        <PalettePanel title="Folder Upload" isOpen={true}>
            <FolderUpload close={closeLeftPanel}/>
        </PalettePanel>
    );
};

// Sidebar icons for folder page
const FolderSidebarIcons: React.FC<{startOpen?: boolean}> = ({startOpen = false}) => {
    const {leftPanelWidth, openLeftPanel, closeLeftPanel, hoverOpenLeftPanel, isPinned} = useDashboardHeader();
    const isOpen = leftPanelWidth > 0;
    const hasInitialized = React.useRef(false);

    // Open panel on mount if startOpen is true
    useEffect(() => {
        if (startOpen && !hasInitialized.current) {
            hasInitialized.current = true;
            // Small delay to ensure context is ready
            const timer = setTimeout(() => {
                openLeftPanel();
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [startOpen, openLeftPanel]);

    const handleClick = useCallback(() => {
        // If already pinned, close it. Otherwise pin it open.
        if (isOpen && isPinned) {
            closeLeftPanel();
        } else {
            openLeftPanel();
        }
    }, [isOpen, isPinned, openLeftPanel, closeLeftPanel]);

    const handleHover = useCallback(() => {
        hoverOpenLeftPanel();
    }, [hoverOpenLeftPanel]);

    return (
        <SidebarIconButton
            icon={<FiUpload/>}
            title="Folder Upload"
            isActive={isOpen}
            onClick={handleClick}
            onMouseEnter={handleHover}
        />
    );
};

const PrimaryFolderPage: React.FC = () => {
    const {getNavHeader} = useNavigationContext()!;
    const {groupFolders} = useFolderContext()!;
    const [loading] = useState(false);
    const [folderPreviews, setFolderPreviews] = useState<Map<string, Array<{id: string; name: string}>>>(new Map());
    const [previewRefreshKey, setPreviewRefreshKey] = useState(0);

    // Re-fetch previews when an upload completes
    useEffect(() => {
        const handler = () => setPreviewRefreshKey(k => k + 1);
        window.addEventListener(DOCUMENT_UPLOAD_COMPLETED, handler);
        return () => window.removeEventListener(DOCUMENT_UPLOAD_COMPLETED, handler);
    }, []);

    // Batch fetch file previews for all folders in one API call
    useEffect(() => {
        if (!groupFolders || groupFolders.length === 0) return;
        const realFolders = groupFolders.filter(f => f.id !== COMBINED_FOLDER_ID);
        if (realFolders.length === 0) return;
        let cancelled = false;
        getFolderPreviews(realFolders.map(f => f.id), 10)
            .then((items) => {
                if (cancelled) return;
                const map = new Map<string, Array<{id: string; name: string}>>();
                for (const item of items) {
                    map.set(item.folder_id, item.files.map(f => ({
                        id: f.id,
                        name: f.title || f.file_name || 'Untitled',
                    })));
                }
                setFolderPreviews(map);
            })
            .catch((err) => { console.error('Folder preview fetch failed:', err); });
        return () => { cancelled = true; };
    }, [groupFolders, previewRefreshKey]);

    const onlyCombined =
        groupFolders &&
        groupFolders.length === 1 &&
        groupFolders[0].id === COMBINED_FOLDER_ID;

    return (
        <DashboardHeader
            defaultPanelWidth={500}
            sidebarIcons={<FolderSidebarIcons startOpen={onlyCombined}/>}
            panelContent={<FolderUploadPanel/>}
        >
            {/* Loader overlay */}
            {loading && (
                <div className="absolute inset-0 flex justify-center items-center z-50 bg-white/70">
                    <Loader/>
                </div>
            )}

            <div className="flex flex-col w-full h-full min-h-0">
                {/* Header + tag manager */}
                <div className="flex shrink-0 justify-between">
                    <div className="flex h-full">
                        {getNavHeader()}
                    </div>
                    <div className="flex h-full">
                        <ManageGroupTags/>
                        <ManageGroupActions/>
                    </div>
                </div>

                {/* Main content */}
                <div className="flex-grow h-full overflow-auto bg-[#fafafb]">
                    {onlyCombined ? (
                        // Welcome / Onboarding Section
                        <div className="flex flex-col items-center justify-center h-full text-center px-6">
                            <div className="mb-6 p-10 rounded-3xl bg-white border border-[#e8e8ec] shadow-[0_12px_40px_-20px_rgba(60,40,110,0.18)] max-w-xl">
                                <div className="flex justify-center mb-5">
                                    <div className="p-4 rounded-2xl bg-[#ede9f7] text-[#564191]">
                                        <FiFolderPlus className="text-4xl"/>
                                    </div>
                                </div>
                                <h1 className="text-4xl font-bold tracking-tight text-[#15151b] mb-3">
                                    Welcome to your workspace
                                </h1>
                                <p className="text-[#4d4d57] text-lg leading-relaxed mb-5">
                                    Get started by creating your first <strong>Folder Upload</strong>.
                                    Each folder mirrors how your files are organized and lets LoA
                                    process, summarize, and make them searchable.
                                </p>
                                <ul className="text-left text-[#6e6e7a] text-base space-y-2 mx-auto max-w-sm list-disc pl-5 marker:text-[#bcabe0]">
                                    <li>Drag and drop your files or folders to upload</li>
                                    <li>We automatically detect duplicates and extract metadata</li>
                                    <li>View progress and summaries in real time</li>
                                    <li>Start chatting with your documents instantly</li>
                                </ul>
                                <div className="mt-6 text-sm text-[#9999a3]">
                                    The panel on the left is open — try uploading a folder now.
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Normal Folder Grid
                        <div className="grid gap-4 p-6 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))] auto-rows-[1fr]">
                            {groupFolders?.map((folder) => (
                                <FolderPreview
                                    key={folder.id}
                                    folder={folder}
                                    size="md"
                                    previewFileNames={folderPreviews.get(folder.id)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </DashboardHeader>
    );
};

export default PrimaryFolderPage;
